/**
 * DSCR Ratio (Estimate) calculator.
 *
 * Lightweight helper to compute Debt Service Coverage Ratio:
 *   ratio = subjectPropertyMonthlyRentalIncome / subjectPropertyHousingExpenses
 *
 * The "Estimated" input is auto-populated from the live division but remains editable,
 * so the broker can hand-tune the final value before saving. On Save, the estimate
 * is returned via onSave for the parent to push into the form.
 */
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField as MuiTextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CurrencyInput from '../../../components/shared/CurrencyInput';

const toNumber = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const fmtRatio = (n) => (n == null ? '—' : n.toFixed(3));

const DscrCalculatorModal = ({ open, onClose, onSave, currentEstimate, defaultHousingExpenses }) => {
  const [rentalIncome, setRentalIncome] = useState(null);
  const [housingExpenses, setHousingExpenses] = useState(null);
  const [estimate, setEstimate] = useState('');
  /** Tracks whether the user has manually edited the estimate; if not, we keep it in lockstep with the computed ratio. */
  const [estimateTouched, setEstimateTouched] = useState(false);

  // Re-seed every time the dialog opens
  useEffect(() => {
    if (!open) return;
    setRentalIncome(null);
    setHousingExpenses(toNumber(defaultHousingExpenses));
    const seedEstimate = toNumber(currentEstimate);
    setEstimate(seedEstimate != null ? String(seedEstimate) : '');
    setEstimateTouched(seedEstimate != null);
  }, [open, currentEstimate, defaultHousingExpenses]);

  const incomeNum = toNumber(rentalIncome);
  const expensesNum = toNumber(housingExpenses);
  const computedRatio =
    incomeNum != null && expensesNum != null && expensesNum !== 0 ? incomeNum / expensesNum : null;

  // Auto-fill the estimate field from the live computation until the user types over it.
  useEffect(() => {
    if (!open) return;
    if (estimateTouched) return;
    setEstimate(computedRatio != null ? computedRatio.toFixed(3) : '');
  }, [computedRatio, estimateTouched, open]);

  const estimateNum = toNumber(estimate);
  const canSave = estimateNum != null && estimateNum > 0;

  const handleSave = () => {
    if (!canSave) return;
    onSave(estimateNum);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Subject property debt service coverage ratio
        <IconButton onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8 }} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 2, mb: 2 }}>
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">Subject property monthly rental income</Typography>
              <Box sx={{ width: 160 }}>
                <CurrencyInput
                  size="small"
                  fullWidth
                  value={rentalIncome ?? ''}
                  onChange={(v) => setRentalIncome(v)}
                  decimalPlaces={2}
                  placeholder="—"
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">/ Subject property housing expenses</Typography>
              <Box sx={{ width: 160 }}>
                <CurrencyInput
                  size="small"
                  fullWidth
                  value={housingExpenses ?? ''}
                  onChange={(v) => setHousingExpenses(v)}
                  decimalPlaces={2}
                  placeholder="—"
                />
              </Box>
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                = Subject property debt service coverage ratio
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {fmtRatio(computedRatio)}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <MuiTextField
          label="Subject property debt service coverage ratio (estimated)"
          type="number"
          size="small"
          fullWidth
          value={estimate}
          onChange={(e) => {
            setEstimate(e.target.value);
            setEstimateTouched(true);
          }}
          slotProps={{ htmlInput: { step: '0.001', min: '0' }, inputLabel: { shrink: true } }}
          placeholder="e.g. 1.250"
          helperText={!canSave ? 'Enter a positive ratio to save.' : ' '}
          error={!canSave && estimate !== ''}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DscrCalculatorModal;
